import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PositionListState, Position, SortOption } from '@/types/positionList.types';
import { PositionListService } from '@/services/positionList.service';
import { sortPositions, filterPositions } from '@/utils/positionListUtils';

interface PositionListActions {
  fetchAreas: () => Promise<void>;
  fetchPositions: (area: string) => Promise<void>;
  refreshPositions: () => Promise<void>;
  setSelectedArea: (area: string) => void;
  setSelectedPosition: (position: Position | null) => void;
  setSortBy: (sortBy: SortOption) => void;
  setSearchQuery: (query: string) => void;
  clearError: () => void;
  getSortedAndFilteredPositions: () => Position[];
}

type PositionListStore = PositionListState & PositionListActions;

export const usePositionListStore = create<PositionListStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      areas: [],
      selectedArea: 'AG',
      positions: [],
      loading: false,
      loadingAreas: false,
      error: null,
      refreshing: false,
      selectedPosition: null,
      sortBy: 'date-desc',
      searchQuery: '',

      // Actions
      fetchAreas: async () => {
        set({ loadingAreas: true, error: null });

        try {
          const areas = await PositionListService.getAreas();
          set({ areas, loadingAreas: false });
        } catch (error) {
          console.error('Error fetching areas:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch areas',
            loadingAreas: false,
          });
        }
      },

      fetchPositions: async (area: string) => {
        // Show loading skeleton
        set({ loading: true, error: null, selectedArea: area });

        try {
          const positions = await PositionListService.getPositionList(area);
          set({
            positions,
            loading: false,
            selectedArea: area,
          });
        } catch (error) {
          console.error(`Error fetching positions for ${area}:`, error);
          set({
            error: error instanceof Error ? error.message : `Failed to fetch positions for ${area}`,
            loading: false,
          });
        }
      },

      refreshPositions: async () => {
        const { selectedArea } = get();

        if (!selectedArea) return;

        set({ refreshing: true, error: null });

        try {
          const positions = await PositionListService.refreshPositionList(selectedArea);
          set({
            positions,
            refreshing: false,
          });
        } catch (error) {
          console.error(`Error refreshing positions for ${selectedArea}:`, error);
          set({
            error:
              error instanceof Error
                ? error.message
                : `Failed to refresh positions for ${selectedArea}`,
            refreshing: false,
          });
        }
      },

      setSelectedArea: (area: string) => {
        const state = get();
        const currentArea = state.selectedArea;

        // Always fetch if switching to a different area
        if (currentArea !== area) {
          set({ selectedArea: area, loading: true, error: null });

          PositionListService.getPositionList(area)
            .then((positions) => {
              set({
                positions,
                loading: false,
              });
            })
            .catch((error) => {
              console.error(`Error fetching positions for ${area}:`, error);
              set({
                error:
                  error instanceof Error ? error.message : `Failed to fetch positions for ${area}`,
                loading: false,
              });
            });
        }
      },

      setSelectedPosition: (position: Position | null) => {
        set({ selectedPosition: position });
      },

      setSortBy: (sortBy: SortOption) => {
        set({ sortBy });
      },

      setSearchQuery: (query: string) => {
        set({ searchQuery: query });
      },

      clearError: () => {
        set({ error: null });
      },

      getSortedAndFilteredPositions: () => {
        const { positions, sortBy, searchQuery } = get();
        const filtered = filterPositions(positions, searchQuery);
        return sortPositions(filtered, sortBy);
      },
    }),
    { name: 'PositionListStore' }
  )
);
